import { FC } from "react"
import { TabsContent, Card, CardHeader, CardTitle, CardDescription, CardContent, Switch } from "../../components/ui"
import { Moon, Sun, Monitor, FileText, Shield } from "lucide-react"
import { useTheme } from "../../hooks/use-theme"
import TermsOfServiceDialog from "../../components/TermsOfServiceDialog"
import PrivacyPolicyDialog from "../../components/PrivacyPolicyDialog"

type UserSettingsTabProps = {

}

const UserSettingsTab: FC<UserSettingsTabProps> = ({ }) => {
  const { theme, toggleTheme } = useTheme()

  return <TabsContent value="user_settings">
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral900 dark:text-neutral100">User Settings</h2>
        <p className="text-neutral600 dark:text-neutral400 mt-1">Manage your personal preferences</p>
      </div>

      {/* Appearance Section */}
      <Card className="dark:bg-neutral800 dark:border-neutral700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-neutral100">
            <Monitor className="text-sumoPrimary" width={20} height={20} />
            Appearance
          </CardTitle>
          <CardDescription className="dark:text-neutral400">
            Customize how the application looks
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dark Mode Toggle */}
          <div className="flex items-center justify-between p-4 bg-neutral50 dark:bg-neutral900 rounded-lg border border-neutral200 dark:border-neutral700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sumoPrimary/10 dark:bg-sumoPrimary/20 flex items-center justify-center">
                {theme === 'dark' ? (
                  <Moon className="text-sumoPrimary" width={20} height={20} />
                ) : (
                  <Sun className="text-sumoPrimary" width={20} height={20} />
                )}
              </div>
              <div>
                <p className="font-semibold text-neutral900 dark:text-neutral100">
                  {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                </p>
                <p className="text-sm text-neutral600 dark:text-neutral400">
                  {theme === 'dark'
                    ? 'Switch to a lighter interface'
                    : 'Switch to a darker interface'}
                </p>
              </div>
            </div>
            <Switch
              checked={theme === 'dark'}
              onCheckedChange={toggleTheme}
              className="data-[state=checked]:bg-sumoPrimary"
            />
          </div>

          <div className="p-3 bg-sumoCream/30 dark:bg-neutral900/50 border border-sumoPrimary/30 dark:border-sumoPrimary/20 rounded-lg">
            <p className="text-xs text-neutral700 dark:text-neutral300 flex items-center gap-2">
              <Monitor width={14} height={14} className="text-sumoPrimary" />
              Theme preference is saved automatically and will persist across sessions
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Legal & Privacy Section */}
      <Card className="dark:bg-neutral800 dark:border-neutral700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-neutral100">
            <FileText className="text-sumoPrimary" width={20} height={20} />
            Legal & Privacy
          </CardTitle>
          <CardDescription className="dark:text-neutral400">
            View our legal documents and privacy information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-neutral50 dark:bg-neutral900 rounded-lg border border-neutral200 dark:border-neutral700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sumoPrimary/10 dark:bg-sumoPrimary/20 flex items-center justify-center">
                <FileText className="text-sumoPrimary" width={20} height={20} />
              </div>
              <div>
                <p className="font-semibold text-neutral900 dark:text-neutral100">
                  Terms of Service
                </p>
                <p className="text-sm text-neutral600 dark:text-neutral400">
                  View our complete terms and conditions
                </p>
              </div>
            </div>
            <TermsOfServiceDialog
              variant="button"
              triggerClassName="text-sumoPrimary hover:text-sumoOrange font-semibold"
              linkText="View Terms"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-neutral50 dark:bg-neutral900 rounded-lg border border-neutral200 dark:border-neutral700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sumoPrimary/10 dark:bg-sumoPrimary/20 flex items-center justify-center">
                <Shield className="text-sumoPrimary" width={20} height={20} />
              </div>
              <div>
                <p className="font-semibold text-neutral900 dark:text-neutral100">
                  Privacy Policy
                </p>
                <p className="text-sm text-neutral600 dark:text-neutral400">
                  Learn how we protect your data
                </p>
              </div>
            </div>
            <PrivacyPolicyDialog
              variant="button"
              triggerClassName="text-sumoPrimary hover:text-sumoOrange font-semibold"
              linkText="View Policy"
            />
          </div>

          <div className="p-3 bg-sumoCream/30 dark:bg-neutral900/50 border border-sumoPrimary/30 dark:border-sumoPrimary/20 rounded-lg">
            <p className="text-xs text-neutral700 dark:text-neutral300 flex items-center gap-2">
              <FileText width={14} height={14} className="text-sumoPrimary" />
              By using InventorySumo, you agree to our Terms of Service and acknowledge our data practices
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  </TabsContent>
}

export default UserSettingsTab
